import { useMemo, useState } from "react";
import { Link } from "wouter";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { gqlRequest } from "../lib/gql";
import {
  CALENDAR_CLUB_EVENTS,
  CALENDAR_OUTINGS,
  MY_CLUB_MEMBERSHIPS,
  MY_EVENT_PARTICIPATIONS,
  MY_FOLLOWEES,
  type CalendarClubEventsResult,
  type CalendarOutingNode,
  type CalendarOutingsResult,
  type MyClubMembershipsResult,
  type MyEventParticipationsResult,
  type MyFolloweesResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { ACTIVITY_ICONS, formatDateRange, type Activity } from "../lib/format";
import { LoginGate } from "../components/LoginGate";
import { ErrorBox } from "../components/ui";
import { Button } from "@/components/ui/button";

// --- local date helpers (the app has no date library; outing.date is a bare
// YYYY-MM-DD and event.startsAt a timestamptz, so all bucketing is local) ---

function ym(d: Date): string {
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, "0")}`;
}

function ymd(d: Date): string {
  return `${ym(d)}-${`${d.getDate()}`.padStart(2, "0")}`;
}

/** Monday-started grid covering the whole month, ends inclusive. */
function monthGridRange(month: Date): { start: Date; end: Date } {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const endOffset = 6 - ((last.getDay() + 6) % 7);
  return {
    start: new Date(month.getFullYear(), month.getMonth(), 1 - startOffset),
    end: new Date(month.getFullYear(), month.getMonth(), last.getDate() + endOffset),
  };
}

function daysBetween(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d));
  return out;
}

type CalendarItem = {
  key: string;
  kind: "outing" | "event";
  href: string;
  title: string;
  source: "own" | "followed" | "event";
  planned: boolean;
  activity: Activity | null;
  /** Owner name for followed outings, club name for events — the day panel's subtitle. */
  context: string | null;
  /** Event start, for intra-day ordering and the day panel's time line. */
  startsAt: string | null;
  endsAt: string | null;
};

const SOURCE_CHIP: Record<CalendarItem["source"], string> = {
  own: "bg-primary/15 text-primary",
  followed: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  event: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};

const SOURCE_DOT: Record<CalendarItem["source"], string> = {
  own: "bg-primary",
  followed: "bg-violet-500",
  event: "bg-emerald-500",
};

export function CalendarPage() {
  return (
    <LoginGate action="gateAction.seeCalendar" next="/calendar">
      <Calendar />
    </LoginGate>
  );
}

function Calendar() {
  const { personId } = useAuth();
  const { t, locale } = useT();
  const today = ymd(new Date());
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selected, setSelected] = useState<string>(today);

  const { start, end } = useMemo(() => monthGridRange(month), [month]);
  const days = useMemo(() => daysBetween(start, end), [start, end]);
  const fromYmd = ymd(start);
  const toYmd = ymd(end);
  // Event window in absolute time: whole grid days, local.
  const fromIso = start.toISOString();
  const toIso = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1).toISOString();

  const followeesQ = useQuery({
    queryKey: ["myFollowees", personId],
    queryFn: () => gqlRequest<MyFolloweesResult>(MY_FOLLOWEES, { viewerId: personId }),
  });
  const followeeIds = useMemo(
    () => (followeesQ.data?.personFollows.nodes ?? []).map((n) => n.followeeId),
    [followeesQ.data],
  );

  const clubsQ = useQuery({
    queryKey: ["myClubMemberships", personId],
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });
  const memberships = useMemo(
    () => (clubsQ.data?.clubMembers.nodes ?? []).filter((m) => m.status === "ACTIVE"),
    [clubsQ.data],
  );
  const clubIds = useMemo(() => memberships.map((m) => m.clubId), [memberships]);

  const ownOutingsQ = useQuery({
    queryKey: ["calendarOutings", "own", personId, fromYmd, toYmd],
    queryFn: () =>
      gqlRequest<CalendarOutingsResult>(CALENDAR_OUTINGS, {
        first: 200,
        filter: {
          date: { greaterThanOrEqualTo: fromYmd, lessThanOrEqualTo: toYmd },
          outingPeopleByOutingId: { some: { personId: { equalTo: personId } } },
        },
      }),
  });

  const followedOutingsQ = useQuery({
    queryKey: ["calendarOutings", "followed", personId, fromYmd, toYmd, followeeIds],
    enabled: followeeIds.length > 0,
    queryFn: () =>
      gqlRequest<CalendarOutingsResult>(CALENDAR_OUTINGS, {
        first: 200,
        filter: {
          date: { greaterThanOrEqualTo: fromYmd, lessThanOrEqualTo: toYmd },
          outingPeopleByOutingId: { some: { role: { equalTo: "OWNER" }, personId: { in: followeeIds } } },
        },
      }),
  });

  const clubEventsQ = useQuery({
    queryKey: ["calendarClubEvents", personId, fromIso, toIso, clubIds],
    enabled: clubIds.length > 0,
    queryFn: () =>
      gqlRequest<CalendarClubEventsResult>(CALENDAR_CLUB_EVENTS, { clubIds, from: fromIso, to: toIso }),
  });

  const participationsQ = useQuery({
    queryKey: ["myEventParticipations", personId],
    queryFn: () => gqlRequest<MyEventParticipationsResult>(MY_EVENT_PARTICIPATIONS, { personId }),
  });

  const clubNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const m of memberships) if (m.club) names.set(m.clubId, m.club.name);
    return names;
  }, [memberships]);

  /** ymd → items, chips ordered outings-then-events, events by start time. */
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    const add = (day: string, item: CalendarItem) => {
      const list = map.get(day) ?? [];
      list.push(item);
      map.set(day, list);
    };

    const seenOutings = new Set<string>();
    const outingSources: [CalendarOutingNode[], "own" | "followed"][] = [
      [ownOutingsQ.data?.outings.nodes ?? [], "own"],
      [followedOutingsQ.data?.outings.nodes ?? [], "followed"],
    ];
    for (const [nodes, source] of outingSources) {
      for (const o of nodes) {
        if (seenOutings.has(o.id)) continue;
        seenOutings.add(o.id);
        const owner = o.outingPeopleByOutingId.nodes.find((p) => p.role === "OWNER");
        add(o.date, {
          key: `outing:${o.id}`,
          kind: "outing",
          href: `/outing/${o.id}`,
          title: o.title,
          source,
          planned: o.status === "PLANNED",
          activity: o.activity,
          context: source === "followed" ? (owner?.displayName ?? null) : null,
          startsAt: null,
          endsAt: null,
        });
      }
    }

    const seenEvents = new Set<string>();
    const events: { id: string; title: string; startsAt: string; endsAt: string | null; clubId: string | null; clubName: string | null; activity: Activity | null }[] = [];
    for (const e of clubEventsQ.data?.events.nodes ?? []) {
      seenEvents.add(e.id);
      events.push({ ...e, clubName: e.club?.name ?? null, activity: e.activities?.[0] ?? null });
    }
    for (const p of participationsQ.data?.eventParticipants.nodes ?? []) {
      if (p.status === "DECLINED" || !p.event || seenEvents.has(p.event.id)) continue;
      seenEvents.add(p.event.id);
      events.push({
        ...p.event,
        clubName: p.event.clubId != null ? (clubNames.get(p.event.clubId) ?? null) : null,
        activity: null,
      });
    }
    for (const e of events) {
      const eventStart = new Date(e.startsAt);
      // Clip multi-day spans to 14 days so bad data can't flood the grid.
      const spanEnd = e.endsAt ? new Date(e.endsAt) : eventStart;
      const clipped = new Date(Math.min(spanEnd.getTime(), eventStart.getTime() + 13 * 86400_000));
      for (const day of daysBetween(
        new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate()),
        new Date(clipped.getFullYear(), clipped.getMonth(), clipped.getDate()),
      )) {
        add(ymd(day), {
          key: `event:${e.id}`,
          kind: "event",
          href: `/event/${e.id}`,
          title: e.title,
          source: "event",
          planned: false,
          activity: e.activity,
          context: e.clubName,
          startsAt: e.startsAt,
          endsAt: e.endsAt,
        });
      }
    }

    for (const list of map.values()) {
      list.sort((a, b) =>
        a.kind === b.kind ? (a.startsAt ?? "").localeCompare(b.startsAt ?? "") : a.kind === "outing" ? -1 : 1,
      );
    }
    return map;
  }, [ownOutingsQ.data, followedOutingsQ.data, clubEventsQ.data, participationsQ.data, clubNames]);

  const monthTitle = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(month),
    [locale, month],
  );
  // 2024-01-01 is a Monday, matching the grid's week start.
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, i + 1)));
  }, [locale]);
  const dayLabelFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }),
    [locale],
  );

  const error =
    ownOutingsQ.error ?? followedOutingsQ.error ?? clubEventsQ.error ?? participationsQ.error ?? followeesQ.error ?? clubsQ.error;
  const fetching =
    ownOutingsQ.isFetching || followedOutingsQ.isFetching || clubEventsQ.isFetching || participationsQ.isFetching;
  const monthHasItems = [...byDay.keys()].some((d) => d.startsWith(ym(month)));
  const selectedItems = byDay.get(selected) ?? [];

  const shiftMonth = (delta: number) => {
    const next = new Date(month.getFullYear(), month.getMonth() + delta, 1);
    setMonth(next);
    setSelected(today.startsWith(ym(next)) ? today : ymd(next));
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-foreground">
          <CalendarDays aria-hidden className="size-6 text-primary" />
          {monthTitle}
        </h1>
        {fetching && (
          <span aria-hidden className="size-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" size="icon" aria-label={t("calendar.prevMonth")} onClick={() => shiftMonth(-1)}>
            <ChevronLeft aria-hidden className="size-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const now = new Date();
              setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
              setSelected(today);
            }}
          >
            {t("calendar.today")}
          </Button>
          <Button variant="outline" size="icon" aria-label={t("calendar.nextMonth")} onClick={() => shiftMonth(1)}>
            <ChevronRight aria-hidden className="size-4" />
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {(["own", "followed", "event"] as const).map((source) => (
          <span key={source} className="flex items-center gap-1.5">
            <span aria-hidden className={`size-2 rounded-full ${SOURCE_DOT[source]}`} />
            {t(source === "own" ? "calendar.legendOwn" : source === "followed" ? "calendar.legendFollowed" : "calendar.legendEvent")}
          </span>
        ))}
      </div>

      {error != null && <ErrorBox error={error} />}

      <div className="overflow-hidden rounded-xl border bg-card shadow-card">
        <div className="grid grid-cols-7 border-b bg-muted/40 text-center text-xs font-medium text-muted-foreground">
          {weekdays.map((w) => (
            <div key={w} className="py-2">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const key = ymd(day);
            const inMonth = day.getMonth() === month.getMonth();
            const items = byDay.get(key) ?? [];
            const isToday = key === today;
            const isSelected = key === selected;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelected(key)}
                aria-pressed={isSelected}
                aria-label={items.length > 0 ? `${dayLabelFmt.format(day)} (${items.length})` : dayLabelFmt.format(day)}
                className={`flex min-h-16 flex-col items-stretch gap-1 border-b border-r p-1 text-left align-top transition-colors last:border-r-0 sm:min-h-24 sm:p-1.5 [&:nth-child(7n)]:border-r-0 [&:nth-last-child(-n+7)]:border-b-0 ${
                  inMonth ? "" : "bg-muted/30"
                } ${isSelected ? "bg-primary/5 ring-1 ring-inset ring-primary/40" : "hover:bg-accent/50"}`}
              >
                <span
                  className={`self-end text-xs tabular-nums ${
                    isToday
                      ? "flex size-5 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground"
                      : inMonth
                        ? "text-foreground"
                        : "text-muted-foreground"
                  }`}
                >
                  {day.getDate()}
                </span>
                {/* Mobile: dots. Desktop: chips. */}
                <span className="flex flex-wrap gap-0.5 sm:hidden">
                  {items.slice(0, 4).map((item) => (
                    <span key={item.key} aria-hidden className={`size-1.5 rounded-full ${SOURCE_DOT[item.source]}`} />
                  ))}
                </span>
                <span className="hidden flex-col gap-0.5 sm:flex">
                  {items.slice(0, 3).map((item) => (
                    <span
                      key={item.key}
                      className={`truncate rounded px-1 py-0.5 text-[11px] leading-tight ${SOURCE_CHIP[item.source]} ${
                        item.planned ? "border border-dashed border-primary/50" : ""
                      }`}
                    >
                      {item.title}
                    </span>
                  ))}
                  {items.length > 3 && (
                    <span className="px-1 text-[11px] text-muted-foreground">
                      {t("calendar.more", { count: items.length - 3 })}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!monthHasItems && !fetching && error == null && (
        <p className="mt-4 text-sm text-muted-foreground">{t("calendar.monthEmpty")}</p>
      )}

      <div className="mt-6">
        <h2 className="mb-3 text-lg font-semibold tracking-tight text-foreground">
          {dayLabelFmt.format(new Date(`${selected}T00:00:00`))}
        </h2>
        {selectedItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("calendar.dayEmpty")}</p>
        ) : (
          <ul className="space-y-2">
            {selectedItems.map((item) => {
              const Icon = item.activity != null ? ACTIVITY_ICONS[item.activity] : CalendarDays;
              return (
                <li key={item.key}>
                  <Link
                    href={item.href}
                    className="flex items-center gap-3 rounded-xl border bg-card p-3 shadow-card transition-shadow hover:shadow-card-hover"
                  >
                    <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${SOURCE_CHIP[item.source]}`}>
                      <Icon aria-hidden className="size-4.5" />
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 truncate text-sm font-medium text-foreground">
                        {item.title}
                        {item.planned && (
                          <span className="shrink-0 rounded border border-dashed border-primary/50 px-1 text-[10px] uppercase tracking-wide text-primary">
                            {t("enum.PLANNED")}
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {item.kind === "event" ? formatDateRange(item.startsAt, item.endsAt) : null}
                        {item.kind === "event" && item.context ? " · " : null}
                        {item.context}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
